const IPV4_REGEX = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const NON_PUBLIC_RANGES: Array<{ base: string; bits: number }> = [
  { base: '0.0.0.0', bits: 8 },
  { base: '10.0.0.0', bits: 8 },
  { base: '100.64.0.0', bits: 10 },
  { base: '127.0.0.0', bits: 8 },
  { base: '169.254.0.0', bits: 16 },
  { base: '172.16.0.0', bits: 12 },
  { base: '192.0.0.0', bits: 24 },
  { base: '192.0.2.0', bits: 24 },
  { base: '192.168.0.0', bits: 16 },
  { base: '198.18.0.0', bits: 15 },
  { base: '198.51.100.0', bits: 24 },
  { base: '203.0.113.0', bits: 24 },
  { base: '224.0.0.0', bits: 4 },
  { base: '240.0.0.0', bits: 4 },
];

const toNumber = (ip: string): number =>
  ip.split('.').reduce((total, octet) => total * 256 + Number(octet), 0);

export const isValidIpv4 = (value?: string | null): boolean =>
  IPV4_REGEX.test(String(value ?? '').trim());

export const isPublicIpv4 = (value?: string | null): boolean => {
  const ip = String(value ?? '').trim();
  if (!isValidIpv4(ip)) return false;

  const address = toNumber(ip);
  return !NON_PUBLIC_RANGES.some(({ base, bits }) => {
    const size = 2 ** (32 - bits);
    return Math.floor(address / size) === Math.floor(toNumber(base) / size);
  });
};

const parseIpv6Groups = (part: string): number[] | null => {
  if (part === '') return [];

  const groups: number[] = [];
  const chunks = part.split(':');
  for (let i = 0; i < chunks.length; i += 1) {
    if (!/^[0-9a-fA-F]{1,4}$/.test(chunks[i])) return null;
    groups.push(parseInt(chunks[i], 16));
  }
  return groups;
};

const parseIpv6 = (value?: string | null): number[] | null => {
  const ip = String(value ?? '').trim();
  if (!ip || ip.indexOf(':') === -1 || /[%[\]]/.test(ip)) return null;

  let work = ip;
  const lastColon = ip.lastIndexOf(':');
  const lastPart = ip.slice(lastColon + 1);
  if (lastPart.indexOf('.') !== -1) {
    if (!isValidIpv4(lastPart)) return null;
    const embedded = toNumber(lastPart);
    const high = Math.floor(embedded / 65536).toString(16);
    const low = (embedded % 65536).toString(16);
    work = `${ip.slice(0, lastColon + 1)}${high}:${low}`;
  }

  if (!/^[0-9a-fA-F:]+$/.test(work)) return null;

  const doubleColon = work.indexOf('::');
  if (doubleColon === -1) {
    const groups = parseIpv6Groups(work);
    return groups && groups.length === 8 ? groups : null;
  }

  if (work.indexOf('::', doubleColon + 1) !== -1) return null;

  const left = parseIpv6Groups(work.slice(0, doubleColon));
  const right = parseIpv6Groups(work.slice(doubleColon + 2));
  if (!left || !right) return null;

  const missing = 8 - (left.length + right.length);
  if (missing < 1) return null;

  return [...left, ...Array.from({ length: missing }, () => 0), ...right];
};

export const isValidIpv6 = (value?: string | null): boolean => parseIpv6(value) !== null;

export const isPublicIpv6 = (value?: string | null): boolean => {
  const groups = parseIpv6(value);
  if (!groups) return false;

  if (groups.every((group) => group === 0)) return false;
  if (groups.slice(0, 7).every((group) => group === 0) && groups[7] === 1) return false;

  if (groups.slice(0, 5).every((group) => group === 0) && groups[5] === 0xffff) {
    const embedded = `${Math.floor(groups[6] / 256)}.${groups[6] % 256}.${Math.floor(
      groups[7] / 256,
    )}.${groups[7] % 256}`;
    return isPublicIpv4(embedded);
  }

  if (groups[0] >= 0xfe80 && groups[0] <= 0xfebf) return false;
  if (groups[0] >= 0xfc00 && groups[0] <= 0xfdff) return false;
  if (groups[0] >= 0xff00) return false;
  if (groups[0] === 0x2001 && groups[1] === 0x0db8) return false;

  return true;
};

export const isValidIp = (value?: string | null): boolean =>
  isValidIpv4(value) || isValidIpv6(value);

export const isPublicIp = (value?: string | null): boolean =>
  isValidIpv4(String(value ?? '').trim()) ? isPublicIpv4(value) : isPublicIpv6(value);

export const normalizeIp = (value?: string | null): string => {
  const ip = String(value ?? '').trim();
  if (isValidIpv4(ip)) return ip;

  const groups = parseIpv6(ip);
  return groups ? groups.map((group) => group.toString(16)).join(':') : ip.toLowerCase();
};
